import React, { createContext, useContext } from "react";
import type { BaseEntity } from "../../UniversalCRMTable/types";
import type { DataState, EntityPageNavigation, PaginationModeContextValue } from "./types";

/**
 * UniversalEntityPageDataContext
 *
 * Contains only data-related state:
 * - dataState: Current data, loading state, error state
 * - paginationModeState: Unified pagination mode state (page, pageSize, cursor, mode)
 * - navigation: router, searchParams, apollo client, active view id
 *
 * Separated from other contexts to prevent unnecessary re-renders
 * when handlers or UI state changes. In particular this context does NOT
 * carry `filterState`, so a keystroke in the search box leaves it untouched.
 */
export interface UniversalEntityPageDataContextValue<_T extends BaseEntity = BaseEntity> {
  dataState: DataState;
  paginationModeState: PaginationModeContextValue;
  navigation: EntityPageNavigation;
}

const UniversalEntityPageDataContext = createContext<
  UniversalEntityPageDataContextValue<BaseEntity> | undefined
>(undefined);

export function UniversalEntityPageDataProvider<T extends BaseEntity>({
  children,
  value,
}: {
  children: React.ReactNode;
  value: UniversalEntityPageDataContextValue<T>;
}) {
  return (
    <UniversalEntityPageDataContext.Provider value={value}>
      {children}
    </UniversalEntityPageDataContext.Provider>
  );
}

/** Non-throwing read — `null` outside a provider. */
export function useOptionalUniversalEntityPageDataContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageDataContextValue<T> | null {
  const context = useContext(UniversalEntityPageDataContext);
  return (context as UniversalEntityPageDataContextValue<T> | undefined) ?? null;
}

export function useUniversalEntityPageDataContext<
  T extends BaseEntity = BaseEntity,
>(): UniversalEntityPageDataContextValue<T> {
  const context = useOptionalUniversalEntityPageDataContext<T>();
  if (!context) {
    throw new Error(
      "useUniversalEntityPageDataContext must be used within UniversalEntityPageDataProvider"
    );
  }
  return context;
}
